import { useState } from "react";
import { Box, Text, useThemeUI } from "theme-ui";

import { useTranslation } from "hooks";
import { Select } from "components";
import flexMap from "./flexMap";
import getRecommendedFlex from "./getRecommendedFlex";
import * as styles from "./FlexGuide.styles";

const WEIGHTS = [
  { value: "40", label: "< 40 lbs." },
  { value: "45", label: "< 45 lbs." },
  { value: "50", label: "< 50 lbs." },
  { value: "55", label: "< 55 lbs." },
  { value: "60", label: "< 60 lbs." },
  { value: "65", label: "< 65 lbs." },
  { value: "70", label: "< 70 lbs." },
  { value: "75", label: "< 75 lbs." },
  { value: "80", label: "< 80 lbs." },
  { value: "85", label: "< 85 lbs." },
  { value: "90", label: "< 90 lbs." },
  { value: "95", label: "< 95 lbs." },
  { value: "100", label: "100 lbs." },
  { value: "105", label: "105 lbs." },
  { value: "110", label: "110 lbs." },
  { value: "115", label: "115 lbs." },
  { value: "120", label: "120 lbs." },
  { value: "125", label: "125 lbs." },
  { value: "130", label: "130 lbs." },
  { value: "135", label: "135 lbs." },
  { value: "140", label: "140 lbs." },
  { value: "145", label: "145 lbs." },
  { value: "150", label: "150 lbs." },
  { value: "155", label: "155 lbs." },
  { value: "160", label: "160 lbs." },
  { value: "165", label: "165 lbs." },
  { value: "170", label: "170 lbs." },
  { value: "175", label: "175 lbs." },
  { value: "180", label: "180 lbs." },
  { value: "185", label: "185 lbs." },
  { value: "190", label: "190 lbs." },
  { value: "195", label: "195 lbs." },
  { value: "200", label: "200 lbs." },
  { value: "205", label: "205 lbs." },
  { value: "210", label: "210 lbs." },
  { value: "215", label: "215 lbs." },
  { value: "220", label: "220 lbs." },
  { value: "225", label: "225 lbs." },
  { value: "230", label: "230 lbs." },
  { value: "235", label: "235 lbs." },
  { value: "240", label: "240 lbs." },
  { value: "245", label: "245 lbs." },
  { value: "250", label: "250 lbs." },
  { value: "255", label: "255 lbs." },
  { value: "260", label: "260 lbs." },
  { value: "265", label: "265 lbs." },
  { value: "270", label: "270 lbs." },
  { value: "275", label: "275 lbs." },
  { value: "280", label: "280 lbs." },
  { value: "285", label: "285 lbs." },
  { value: "290", label: "290 lbs." },
  { value: "295", label: "295 lbs." },
  { value: "300", label: "300 lbs." },
  { value: "301", label: "> 300 lbs." },
];

const HEIGHTS = [
  { value: "101.6", label: "< 3'4\"" },
  { value: "104.14", label: "< 3'5\"" },
  { value: "106.68", label: "< 3'6\"" },
  { value: "109.22", label: "< 3'7\"" },
  { value: "111.76", label: "< 3'8\"" },
  { value: "114.3", label: "< 3'9\"" },
  { value: "116.84", label: "< 3'10\"" },
  { value: "119.38", label: "< 3'11\"" },
  { value: "121.92", label: "< 4'\"" },
  { value: "124.46", label: "4'1\"" },
  { value: "127", label: "4'2\"" },
  { value: "129.54", label: "4'3\"" },
  { value: "132.08", label: "4'4\"" },
  { value: "134.62", label: "4'5\"" },
  { value: "137.16", label: "4'6\"" },
  { value: "139.7", label: "4'7\"" },
  { value: "142.24", label: "4'8\"" },
  { value: "144.78", label: "4'9\"" },
  { value: "147.32", label: "4'10\"" },
  { value: "149.86", label: "4'11\"" },
  { value: "152.4", label: "5'" },
  { value: "154.94", label: "5'1\"" },
  { value: "157.48", label: "5'2\"" },
  { value: "160.02", label: "5'3\"" },
  { value: "162.56", label: "5'4\"" },
  { value: "165.1", label: "5'5\"" },
  { value: "167.64", label: "5'6\"" },
  { value: "170.18", label: "5'7\"" },
  { value: "172.72", label: "5'8\"" },
  { value: "175.26", label: "5'9\"" },
  { value: "177.8", label: "5'10\"" },
  { value: "180.34", label: "5'11\"" },
  { value: "182.88", label: "6'" },
  { value: "185.42", label: "6'1\"" },
  { value: "187.96", label: "6'2\"" },
  { value: "190.5", label: "6'3\"" },
  { value: "193.04", label: "6'4\"" },
  { value: "195.58", label: "6'5\"" },
  { value: "198.12", label: "6'6\"" },
  { value: "200.66", label: "6'7\"" },
  { value: "203.2", label: "6'8\"" },
  { value: "204", label: "> 6'8\"" },
];

export default function FlexGuide() {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const { t } = useTranslation();

  const [approxWeight, setApproxWeight] = useState("");
  const [approxHeight, setApproxHeight] = useState("");

  const recommendedFlex =
    approxWeight && approxHeight
      ? getRecommendedFlex({
          weight: approxWeight,
          height: approxHeight,
          flexMap,
        })
      : "--";

  const inputSize = isMobile ? "medium" : "large";

  return (
    <Box>
      <Box sx={styles.selects}>
        <Box sx={styles.select}>
          <Select
            variant="round"
            size={inputSize}
            value={approxWeight}
            onChange={({ target }) => setApproxWeight(target.value)}
          >
            <option key="__blank" value="" disabled={approxWeight}>
              {t("Approx. Weight")}
            </option>
            {WEIGHTS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Box>
        <Box sx={styles.select}>
          <Select
            variant="round"
            size={inputSize}
            value={approxHeight}
            onChange={({ target }) => setApproxHeight(target.value)}
          >
            <option key="__blank" value="" disabled={approxHeight}>
              {t("Approx. Height")}
            </option>
            {HEIGHTS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Box>
      </Box>

      <Box sx={styles.recommendation}>
        <Text variant="h5Light">{t("Your Recommended Flex is")}</Text>
        <Text variant="h1">{recommendedFlex}</Text>
      </Box>
    </Box>
  );
}
